'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { generateQuestsForRole, buildQuestContext, GeneratedQuest } from '@/lib/questEngine';
import { getUserResonanceSummary, getUserBookmarks } from '@/lib/bookmarks';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type QuestFilter = 'all' | 'active' | 'completed';
type DifficultyFilter = 'all' | 'entry' | 'intermediate' | 'deep';

interface SavedQuest {
  id: string;
  quest_title: string;
  quest_desc: string;
  quest_status: string;
  user_role: string;
  tile_id: string;
  ecological_score: number;
  created_at: string;
  completed_at?: string;
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const DIFFICULTY_COLORS: Record<string, string> = {
  entry: '#2d7a47',
  intermediate: '#D4A82A',
  deep: '#8a8ab8',
};

const DIFFICULTY_ICONS: Record<string, string> = {
  entry: '◎',
  intermediate: '✦',
  deep: '⌘',
};

const QUEST_TYPE_ICONS: Record<string, string> = {
  observation: '◎',
  walkthrough: '◈',
  collection: '✦',
  verse: '☽',
  reading: '◈',
  scholarship: '⌘',
  mapping: '⬡',
  justice: '⌘',
  'community-observation': '⬡',
  'justice-mapping': '⌘',
  'resource-mapping': '⬡',
  framework: '✦',
  bridge: '◈',
  annotation: '◈',
  etymology: '✦',
  'cross-reference': '⌘',
  methodology: '⌘',
  onboarding: '◈',
  exploration: '◎',
  'role-selection': '⌘',
  listing: '⬡',
  networking: '⬡',
};

const REWARDS: Record<string, { label: string; icon: string; color: string }> = {
  entry: { label: 'First Step', icon: '◎', color: '#2d7a47' },
  intermediate: { label: 'Deeper Seeker', icon: '✦', color: '#D4A82A' },
  deep: { label: 'Scholar of Haal', icon: '⌘', color: '#8a8ab8' },
};

// ─── QUEST CARD ───────────────────────────────────────────────────────────────

function QuestCard({
  quest,
  difficulty,
  onComplete,
  isCompleted,
}: {
  quest: GeneratedQuest;
  difficulty: 'entry' | 'intermediate' | 'deep';
  onComplete?: () => void;
  isCompleted?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const diffColor = DIFFICULTY_COLORS[difficulty];
  const reward = REWARDS[difficulty];

  return (
    <div
      className="rounded-xl transition-all duration-300"
      style={{
        background: isCompleted ? 'rgba(45,122,71,0.04)' : `${quest.color}06`,
        border: `1px solid ${isCompleted ? 'rgba(45,122,71,0.2)' : quest.color + '25'}`,
        borderLeft: `3px solid ${isCompleted ? '#2d7a47' : quest.color}`,
        opacity: isCompleted ? 0.75 : 1,
      }}
    >
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-3">
          <span className="text-lg flex-shrink-0 mt-0.5" style={{ color: isCompleted ? '#2d7a47' : quest.color }}>
            {isCompleted ? '✓' : (QUEST_TYPE_ICONS[quest.quest_type] || quest.icon)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-mono text-sm font-black uppercase tracking-tight" style={{ color: isCompleted ? 'rgba(245,240,232,0.5)' : '#F5F0E8' }}>
                {quest.title}
              </h3>
              {isCompleted && (
                <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.15)', color: '#2d7a47', border: '1px solid rgba(45,122,71,0.3)' }}>
                  Completed
                </span>
              )}
            </div>
            {/* Difficulty + reward badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5"
                style={{ background: `${diffColor}12`, border: `1px solid ${diffColor}30`, color: diffColor }}
              >
                {DIFFICULTY_ICONS[difficulty]} {difficulty}
              </span>
              <span
                className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5"
                style={{ background: `${reward.color}10`, border: `1px solid ${reward.color}25`, color: reward.color }}
              >
                {reward.icon} {reward.label} Badge
              </span>
              <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>
                {quest.module_target}
              </span>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 font-mono text-[9px] uppercase tracking-widest px-2 py-1 transition-all duration-200"
            style={{ color: 'rgba(212,197,169,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>

        {/* Progress bar — visual only, entry=33%, intermediate=66%, deep=100% */}
        <div className="h-0.5 rounded-full mb-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: isCompleted ? '100%' : difficulty === 'entry' ? '33%' : difficulty === 'intermediate' ? '66%' : '90%',
              background: isCompleted ? '#2d7a47' : quest.color,
            }}
          />
        </div>

        {/* Description */}
        <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.7 }}>
          {quest.description}
        </p>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 pt-4 space-y-3" style={{ borderTop: `1px solid ${quest.color}15` }}>
            {/* Resonance hook */}
            <div>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Resonance Hook</p>
              <p className="font-mono text-[10px]" style={{ color: `${quest.color}80` }}>{quest.resonance_hook}</p>
            </div>
            {/* Amanah prompt */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(139,105,20,0.06)', border: '1px solid rgba(139,105,20,0.15)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>Amanah Prompt</p>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, fontStyle: 'italic', lineHeight: 1.6 }}>
                "{quest.amanah_prompt}"
              </p>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-3">
              <Link
                href={quest.href}
                className="font-mono text-[10px] tracking-widest uppercase px-4 py-2 transition-colors"
                style={{ background: `${quest.color}15`, color: quest.color, border: `1px solid ${quest.color}30` }}
              >
                Begin Quest →
              </Link>
              {!isCompleted && onComplete && (
                <button
                  onClick={onComplete}
                  className="font-mono text-[10px] tracking-widest uppercase px-4 py-2 transition-colors"
                  style={{ background: 'rgba(45,122,71,0.1)', color: '#2d7a47', border: '1px solid rgba(45,122,71,0.3)' }}
                >
                  Mark Complete ✓
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function QuestsPage() {
  const [role, setRole] = useState('explorer');
  const [activeFilter, setActiveFilter] = useState<QuestFilter>('active');
  const [diffFilter, setDiffFilter] = useState<DifficultyFilter>('all');
  const [activeQuests, setActiveQuests] = useState<GeneratedQuest[]>([]);
  const [completedQuests, setCompletedQuests] = useState<GeneratedQuest[]>([]);
  const [historyLog, setHistoryLog] = useState<SavedQuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const supabase = createClient();

  const loadQuests = useCallback(async () => {
    setLoading(true);
    const savedRole = typeof window !== 'undefined' ? localStorage.getItem('haal_role') || 'explorer' : 'explorer';
    setRole(savedRole);

    const [summary, bookmarks] = await Promise.all([
      getUserResonanceSummary(),
      getUserBookmarks(),
    ]);

    // Load history for context
    const sessionId = typeof window !== 'undefined' ? sessionStorage.getItem('haal_session') || '' : '';
    const { data: { user } } = await supabase.auth.getUser();
    let histQuery = supabase.from('content_resonance').select('*').order('created_at', { ascending: false }).limit(50);
    if (user) histQuery = histQuery.eq('user_id', user.id);
    else histQuery = histQuery.eq('session_id', sessionId);
    const { data: histData } = await histQuery;

    // Load module visits
    let visitsQuery = supabase.from('module_visits').select('module_id, visit_count');
    if (user) visitsQuery = visitsQuery.eq('user_id', user.id);
    else visitsQuery = visitsQuery.eq('session_id', sessionId);
    const { data: visitsData } = await visitsQuery;
    const visitMap: Record<string, number> = {};
    (visitsData || []).forEach((v: any) => { visitMap[v.module_id] = v.visit_count; });

    const context = buildQuestContext(
      savedRole,
      summary || { topContentTypes: [], topNames: [], topKeys: [], totalInteractions: 0 },
      histData || [],
      bookmarks.length,
      visitMap,
    );

    const generated = await generateQuestsForRole(savedRole, context, 5);
    setActiveQuests(generated);

    // Load completed quests from Supabase
    if (user) {
      const { data: questData } = await supabase
        .from('caretaker_quests')
        .select('*')
        .eq('user_role', savedRole)
        .order('created_at', { ascending: false })
        .limit(30);
      const completed = (questData || []).filter((q: SavedQuest) => q.quest_status === 'completed');
      setHistoryLog(questData || []);
      setCompletedIds(new Set(completed.map((q: SavedQuest) => q.quest_title)));
    }

    setLoading(false);
  }, [supabase]);

  useEffect(() => { loadQuests(); }, [loadQuests]);

  const handleComplete = async (quest: GeneratedQuest) => {
    setCompletedIds(prev => new Set([...prev, quest.title]));
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('caretaker_quests').insert({
        user_role: role,
        ward: 'personal',
        city: 'platform',
        tile_id: quest.module_target,
        quest_title: quest.title,
        quest_desc: quest.description,
        ecological_score: quest.difficulty === 'entry' ? 1 : quest.difficulty === 'intermediate' ? 2 : 3,
        social_score: quest.difficulty === 'entry' ? 1 : quest.difficulty === 'intermediate' ? 2 : 3,
        quest_status: 'completed',
      });
    }
  };

  const roleLabel = role.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const filteredActive = activeQuests.filter(q => {
    if (diffFilter !== 'all' && q.difficulty !== diffFilter) return false;
    return true;
  });

  const completedQuestsList = activeQuests.filter(q => completedIds.has(q.title));
  const incompleteQuests = filteredActive.filter(q => !completedIds.has(q.title));

  const displayQuests = activeFilter === 'completed' ? completedQuestsList : activeFilter === 'active' ? incompleteQuests : filteredActive;

  const completionRate = activeQuests.length > 0 ? Math.round((completedIds.size / activeQuests.length) * 100) : 0;

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 md:px-6 pt-24 pb-20">

        {/* Page header */}
        <div className="mb-8">
          <p className="font-mono text-xs tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>⌘ Quest Log</p>
          <h1 className="font-mono text-3xl font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>
            Active Quests
          </h1>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13 }}>
            Personalized for <span style={{ color: '#c9a84c' }}>{roleLabel}</span> — calibrated to your resonance patterns and interaction history.
          </p>

          {/* Path of Knowledge Hadith — grounding hadith for the quest system */}
          <div className="mt-6 rounded-xl p-5" style={{ background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.2)', borderLeft: '3px solid rgba(45,122,71,0.4)' }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>
              Narrated Abu Hurairah · Jami at-Tirmidhi 2646 · Grade: Sahih (Sound)
            </p>
            <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 13, lineHeight: 1.8, fontStyle: 'italic' }}>
              "Whoever takes a path upon which to obtain knowledge, Allah makes the path to Paradise easy for him."
            </p>
            <p className="font-mono text-[8px] uppercase tracking-widest mt-3" style={{ color: 'rgba(45,122,71,0.4)' }}>
              Every quest is a path. Every path upon which knowledge is sought is eased toward its destination.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Sidebar */}
          <aside className="lg:w-56 flex-shrink-0 space-y-4">
            {/* Role card */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.3)' }}>Your Role</p>
              <p className="font-mono text-sm font-black uppercase" style={{ color: '#c9a84c' }}>{roleLabel}</p>
              <Link href="/onboarding" className="font-mono text-[9px] uppercase tracking-widest mt-2 block" style={{ color: 'rgba(212,197,169,0.3)' }}>
                Change role →
              </Link>
            </div>

            {/* Progress */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Quest Progress</p>
              <div className="flex justify-between items-center mb-2">
                <span style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>Completed</span>
                <span className="font-mono text-sm font-black" style={{ color: '#c9a84c' }}>{completedIds.size}/{activeQuests.length}</span>
              </div>
              <div className="h-1.5 rounded-full mb-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${completionRate}%`, background: 'linear-gradient(90deg, #2d7a47, #D4A82A)' }} />
              </div>
              <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>{completionRate}% complete</p>
            </div>

            {/* Difficulty filter */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Difficulty</p>
              {(['all', 'entry', 'intermediate', 'deep'] as DifficultyFilter[]).map(d => (
                <button
                  key={d}
                  onClick={() => setDiffFilter(d)}
                  className="w-full text-left font-mono text-[9px] uppercase tracking-widest px-2 py-1.5 rounded mb-1 transition-all duration-200"
                  style={{
                    background: diffFilter === d ? 'rgba(201,168,76,0.08)' : 'transparent',
                    color: diffFilter === d ? '#c9a84c' : 'rgba(212,197,169,0.35)',
                    borderLeft: diffFilter === d ? '2px solid #c9a84c' : '2px solid transparent',
                  }}
                >
                  {d === 'all' ? '◈ All Levels' : `${DIFFICULTY_ICONS[d]} ${d}`}
                </button>
              ))}
            </div>

            {/* Links */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Navigate</p>
              {[
                { label: 'My Dashboard', href: '/user-dashboard' },
                { label: 'Community', href: '/community' },
                { label: 'Guided Walkthrough', href: '/guided-walkthrough' },
              ].map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="block font-mono text-[9px] uppercase tracking-widest py-1.5 transition-colors"
                  style={{ color: 'rgba(212,197,169,0.35)', borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#c9a84c')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(212,197,169,0.35)')}
                >
                  → {link.label}
                </Link>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* Filter tabs */}
            <div className="flex gap-1 mb-6">
              {([
                { id: 'active', label: `Active (${incompleteQuests.length})` },
                { id: 'completed', label: `Completed (${completedIds.size})` },
                { id: 'all', label: `All (${activeQuests.length})` },
              ] as { id: QuestFilter; label: string }[]).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 transition-all duration-200"
                  style={{
                    background: activeFilter === tab.id ? 'rgba(201,168,76,0.1)' : 'transparent',
                    border: `1px solid ${activeFilter === tab.id ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.06)'}`,
                    color: activeFilter === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.35)',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.02)' }} />
                ))}
              </div>
            ) : displayQuests.length === 0 ? (
              <div className="text-center py-16 rounded-xl" style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <p className="font-mono text-2xl mb-3" style={{ color: 'rgba(212,197,169,0.2)' }}>◈</p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 14 }}>
                  {activeFilter === 'completed' ? 'No completed quests yet.' : 'No quests match this filter.'}
                </p>
                {activeFilter === 'completed' && (
                  <p style={{ color: 'rgba(212,197,169,0.25)', fontSize: 12, marginTop: 8 }}>
                    Complete a quest to see it here.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {displayQuests.map((quest, idx) => (
                  <QuestCard
                    key={`${quest.title}-${idx}`}
                    quest={quest}
                    difficulty={quest.difficulty}
                    isCompleted={completedIds.has(quest.title)}
                    onComplete={() => handleComplete(quest)}
                  />
                ))}
              </div>
            )}

            {/* Historical quest log */}
            {historyLog.length > 0 && (
              <div className="mt-10">
                <h2 className="font-mono text-sm font-black uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.4)' }}>
                  Historical Quest Log
                </h2>
                <div className="space-y-2">
                  {historyLog.map((q, idx) => (
                    <div
                      key={q.id || idx}
                      className="flex items-center gap-4 p-3 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)' }}
                    >
                      <span
                        className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 flex-shrink-0"
                        style={{
                          background: q.quest_status === 'completed' ? 'rgba(45,122,71,0.12)' : 'rgba(201,168,76,0.08)',
                          color: q.quest_status === 'completed' ? '#2d7a47' : '#c9a84c',
                          border: `1px solid ${q.quest_status === 'completed' ? 'rgba(45,122,71,0.3)' : 'rgba(201,168,76,0.2)'}`,
                        }}
                      >
                        {q.quest_status}
                      </span>
                      <p className="flex-1 font-mono text-[11px] uppercase tracking-wide" style={{ color: 'rgba(245,240,232,0.5)' }}>
                        {q.quest_title}
                      </p>
                      <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.25)' }}>
                        {new Date(q.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
